import React, { useState, useContext, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  CircularProgress,
  Typography,
  useTheme,
  Chip,
  Avatar,
} from "@mui/material";
import { Send, Mic, Stop, AttachFile, Image as ImageIcon, Close } from "@mui/icons-material";
import { ChatContext } from "../context/chatcontext";
import ModelSelectorButton from "./ModelSelectorButton";

export default function ChatInput() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [micError, setMicError] = useState(null);
  const recognitionRef = useRef(null);
  const isStoppingRef = useRef(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const { addMessage, isProcessing, selectedFiles, setSelectedFiles } = useContext(ChatContext);
  const [imagePreviews, setImagePreviews] = useState([]);

  // 🎤 Setup mic
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.warn("🎤 Speech recognition not supported in this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      console.log("🎤 Mic started");
      setListening(true);
      setMicError(null);
      isStoppingRef.current = false;
    };

    recognition.onresult = (event) => {
      console.log("🎤 Mic onresult event:", event);
      let newTranscript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          newTranscript += event.results[i][0].transcript;
        }
      }
      if (newTranscript) {
        console.log("🎤 Final transcript found:", newTranscript);
        setInput((prev) => prev + (prev.endsWith(" ") || prev === "" ? "" : " ") + newTranscript.trim());
      }
    };

    recognition.onerror = (event) => {
      console.error("🎤 Mic error:", event.error);
      setMicError(event.error);
      
      if (event.error === 'not-allowed') {
        alert("Microphone permission denied. Please enable it in browser settings.");
      }
      
      setListening(false);
      isStoppingRef.current = false;
    };

    recognition.onend = () => {
      console.log("🎤 Mic ended");
      // Only set listening to false if we didn't intentionally stop it
      // or if it's not supposed to be continuous
      if (!isStoppingRef.current && recognition.continuous) {
        console.log("🎤 Attempting to restart mic...");
        try {
          recognition.start();
        } catch (e) {
          console.error("🎤 Failed to restart mic:", e);
          setListening(false);
        }
      } else {
        setListening(false);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        isStoppingRef.current = true;
        recognitionRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = () => {
    if (!recognitionRef.current) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (listening) {
      isStoppingRef.current = true;
      recognitionRef.current.stop();
      setListening(false);
    } else {
      try {
        isStoppingRef.current = false;
        recognitionRef.current.start();
      } catch (error) {
        console.error("🎤 Mic start error:", error);
        // If it's already started, we just sync the state
        if (error.name === 'InvalidStateError') {
          setListening(true);
        }
      }
    }
  };

  // 🚀 Send Message
  const handleSend = async () => {
    console.log("📤 handleSend called. Input:", input, "Selected files:", selectedFiles.length);
    if (!input.trim() && selectedFiles.length === 0) {
      console.warn("⚠️ Empty message and no files. Aborting send.");
      return;
    }
    
    // Prevent duplicate sends if already processing
    if (isProcessing) {
      console.warn("⚠️ Message is already being processed. Please wait...");
      return;
    }
    
    const messageContent = input.trim();
    
    // Clear input immediately before sending
    setInput("");
    
    try {
      const userMessage = {
        role: "user",
        content: messageContent,
        files: [...selectedFiles], // Create a copy of the files array
      };

      console.log("📤 Sending message with files:", userMessage.files.length);

      // Send the message which will trigger processing animations
      await addMessage(userMessage);
      
      // ✅ Clear selected files and image previews ONLY after successful send
      setSelectedFiles([]);
      setImagePreviews([]);
    } catch (error) {
      console.error("Error sending message:", error);
      alert(`Failed to send message: ${error.message || "Unknown error"}`);
    }
  };

  // 📎 Handle File Selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    console.log("📎 Files selected via handleFileSelect:", files.map(f => f.name));
    setSelectedFiles((prev) => [...prev, ...files]);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // 🖼️ Handle Image Selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files).filter(file => 
      file.type.startsWith('image/')
    );
    console.log("🖼️ Images selected via handleImageSelect:", files.map(f => f.name));
    
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
      
      // Create previews for images
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, {
            file: file,
            preview: reader.result,
            name: file.name
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
    
    // Reset input so same file can be selected again
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  // 📋 Handle Clipboard Paste (for screenshots)
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    const pastedFiles = [];
    
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }
    
    if (pastedFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...pastedFiles]);
      
      // Create previews for pasted images
      pastedFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews((prev) => [...prev, {
            file: file,
            preview: reader.result,
            name: file.name
          }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // 🗑️ Remove Image Preview
  const removeImagePreview = (index) => {
    const previewToRemove = imagePreviews[index];
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setSelectedFiles((prev) => prev.filter((file) => file !== previewToRemove.file));
  };

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column",
      alignItems: "center",
      py: 2, 
      background: "#ffffff",
      borderTop: "1px solid #e2e8f0",
    }}>
      {/* Image Previews */}
      {imagePreviews.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 2,
            px: 2,
            flexWrap: "wrap",
            gap: 1,
            width: "100%",
          }}
        >
          {imagePreviews.map((preview, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                borderRadius: 2,
                overflow: "hidden",
                border: "2px solid #e2e8f0",
                maxWidth: 120,
                maxHeight: 120,
              }}
            >
              <img
                src={preview.preview}
                alt={preview.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              <IconButton
                size="small"
                onClick={() => removeImagePreview(index)}
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  bgcolor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.8)" },
                  width: 20,
                  height: 20,
                }}
              >
                <Close sx={{ fontSize: "12px" }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          width: { xs: "95%", sm: "85%", md: "75%" },
          maxWidth: "950px",
          borderRadius: "16px",
          px: 2,
          py: 1,
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          {/* 📎 File Upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.js,.jsx,.ts,.tsx,.py,.java,.c,.cpp,.h,.html,.css,.json"
            multiple
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Tooltip title="Attach file">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                sx={{ 
                  color: "#64748b",
                  "&:hover": { color: "#1e3a8a", bgcolor: "rgba(30, 58, 138, 0.05)" },
                }}
              >
                <AttachFile />
              </IconButton>
            </span>
          </Tooltip>

          {/* 🖼️ Image Upload */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleImageSelect}
          />
          <Tooltip title="Attach image">
            <span>
              <IconButton
                onClick={() => imageInputRef.current?.click()}
                disabled={isProcessing}
                sx={{ 
                  color: "#64748b",
                  "&:hover": { color: "#1e3a8a", bgcolor: "rgba(30, 58, 138, 0.05)" },
                }}
              >
                <ImageIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          {/* Show file count */}
          {selectedFiles.length > 0 && (
            <Chip 
              label={`${selectedFiles.length} files`}
              size="small"
              onDelete={() => {
                setSelectedFiles([]);
                setImagePreviews([]);
              }}
              sx={{ bgcolor: "rgba(30, 58, 138, 0.1)", color: "#1e3a8a", fontWeight: 600, fontSize: "0.75rem" }}
            />
          )}

          {/* 📝 Text Field */}
          <TextField
            fullWidth
            placeholder="Ask anything..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            onPaste={handlePaste}
            multiline
            maxRows={4}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
                padding: "8px 4px",
              },
              "& .MuiInputBase-input": {
                color: "#0f172a",
                fontSize: "0.95rem",
                fontWeight: 500,
              }
            }}
          />

          {/* 🎤 Mic */}
          <Tooltip title={micError ? `Error: ${micError}` : (listening ? "Stop Listening" : "Voice input")}>
            <IconButton
              onClick={toggleMic}
              sx={{ 
                color: micError ? "#f87171" : (listening ? "#ef4444" : "#64748b"),
                "&:hover": { bgcolor: "rgba(30, 58, 138, 0.05)" },
              }}
            >
              {listening ? <Stop /> : <Mic />}
            </IconButton>
          </Tooltip>

          {/* 📤 Send */}
          {isProcessing ? (
            <CircularProgress size={24} sx={{ color: "#1e3a8a", mx: 1 }} />
          ) : (
            <IconButton
              onClick={handleSend}
              disabled={isProcessing || (!input.trim() && selectedFiles.length === 0)}
              sx={{
                bgcolor: "#1e3a8a",
                color: "white",
                "&:hover": { bgcolor: "#1e40af" },
                "&:disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
                borderRadius: "12px",
                width: 40,
                height: 40,
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Paper>
      <Typography variant="caption" sx={{ mt: 1, color: "#94a3b8", fontWeight: 500 }}>
        exivis can make mistakes. Check important info.
      </Typography>
    </Box>
  );
}
